import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import type { DocumentCollectionIcon } from '@/graphql/schema'
import type { DocumentCollectionFormValues } from '@/features/document-collection/use-add-document-collection-sheet-controller'
import type { useAppForm } from '@/shared/form/use-app-form'
import {
  DocumentCollectionDescriptionField,
  DocumentCollectionIconField,
  DocumentCollectionNameField,
} from '@/features/document-collection/document-collection-form-fields'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { CollectionCreationChoice } from '@/features/document-collection/collection-creation-choice'
import { CollectionTemplateLibrary } from '@/features/document-collection/collection-template-library'
import { useAddDocumentCollectionSheetController } from '@/features/document-collection/use-add-document-collection-sheet-controller'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/ui/sheet'

type AppForm = ReturnType<typeof useAppForm>
type Translate = ReturnType<typeof useTranslation>['t']
type AddDocumentCollectionSheetProps = {
  onOpenChange?: (open: boolean) => void
  open?: boolean
  showTrigger?: boolean
  trigger?: React.ReactNode
  triggerAsChild?: boolean
}

function AddDocumentCollectionForm({
  form,
  submitError,
}: {
  form: AppForm
  submitError: string | null
}) {
  const { t } = useTranslation('documentCollections')

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="flex flex-col gap-6 px-4"
    >
      <form.AppField name="name">
        {() => <DocumentCollectionNameField />}
      </form.AppField>
      <form.AppField name="description">
        {() => <DocumentCollectionDescriptionField />}
      </form.AppField>
      <FirstFieldControls form={form} t={t} />
      <form.AppField name="icon">
        {(field) => (
          <DocumentCollectionIconField
            value={field.state.value as DocumentCollectionIcon}
            onChange={(nextIcon) => field.handleChange(nextIcon)}
          />
        )}
      </form.AppField>
      {submitError ? (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      ) : null}
      <CollectionFormFooter form={form} t={t} />
    </form>
  )
}

function FirstFieldControls({ form, t }: { form: AppForm; t: Translate }) {
  return (
    <>
      <form.AppField name="createFirstField">
        {(field) => (
          <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="document-collection-create-first-field">
                {t('documentCollections:createFirstField')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('documentCollections:createFirstFieldHint')}
              </p>
            </div>
            <Switch
              id="document-collection-create-first-field"
              checked={field.state.value as boolean}
              onCheckedChange={(checked) => field.handleChange(checked)}
            />
          </div>
        )}
      </form.AppField>
      <form.Subscribe
        selector={(state) =>
          (state.values as DocumentCollectionFormValues).createFirstField
        }
      >
        {(createFirstField) => (
          <form.AppField name="firstFieldName">
            {(field) => {
              const errors = field.state.meta.errors
              const showError = field.state.meta.isTouched && errors.length > 0

              return (
                <div className="space-y-2">
                  <Label htmlFor="document-collection-first-field-name">
                    {t('documentCollections:firstFieldNameLabel')}
                  </Label>
                  <Input
                    id="document-collection-first-field-name"
                    disabled={!createFirstField}
                    value={field.state.value as string}
                    placeholder={t(
                      'documentCollections:firstFieldNamePlaceholder',
                    )}
                    aria-invalid={showError}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  {showError ? (
                    <p className="text-sm text-destructive">
                      {t(getFieldErrorMessage(errors[0]))}
                    </p>
                  ) : null}
                </div>
              )
            }}
          </form.AppField>
        )}
      </form.Subscribe>
    </>
  )
}

function getFieldErrorMessage(error: unknown) {
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }

  return ''
}

function CollectionFormFooter({ form, t }: { form: AppForm; t: Translate }) {
  return (
    <SheetFooter>
      <form.Subscribe
        selector={(state) => ({
          canSubmit: canSubmitCollectionForm(
            state.values as DocumentCollectionFormValues,
            state.isSubmitting,
          ),
        })}
      >
        {({ canSubmit }) => (
          <form.AppForm>
            <form.SubscribeButton
              label={t('documentCollections:addCollection')}
              disabled={!canSubmit}
            />
          </form.AppForm>
        )}
      </form.Subscribe>
      <SheetClose asChild>
        <Button variant="outline" type="button">
          {t('common:cancel')}
        </Button>
      </SheetClose>
    </SheetFooter>
  )
}

function canSubmitCollectionForm(
  values: DocumentCollectionFormValues,
  isSubmitting: boolean,
) {
  return (
    !isSubmitting &&
    values.name.trim().length > 0 &&
    (!values.createFirstField || values.firstFieldName.trim().length > 0)
  )
}

export function AddDocumentCollectionSheet(
  props: AddDocumentCollectionSheetProps,
) {
  const { showTrigger = true, trigger, triggerAsChild = true } = props
  const controller = useAddDocumentCollectionSheetController(props)
  const { t } = useTranslation('documentCollections')
  const [mode, setMode] = React.useState<'choice' | 'manual' | 'template'>(
    'choice',
  )

  const handleOpenChange = (open: boolean) => {
    controller.handleOpenChange(open)
    if (!open) setMode('choice')
  }

  return (
    <Sheet open={controller.open} onOpenChange={handleOpenChange}>
      {showTrigger ? (
        <SheetTrigger asChild={triggerAsChild}>
          {trigger ?? <Button>{t('documentCollections:addCollection')}</Button>}
        </SheetTrigger>
      ) : null}
      <SheetContent className="overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <div className="flex items-center gap-2 pr-8">
            {mode !== 'choice' ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="-ml-2 shrink-0"
                aria-label={t('documentCollections:backToCreationChoice')}
                onClick={() => setMode('choice')}
              >
                <ArrowLeft className="size-4" />
              </Button>
            ) : null}
            <SheetTitle>{getSheetTitle(mode, t)}</SheetTitle>
          </div>
          <SheetDescription>{getSheetDescription(mode, t)}</SheetDescription>
        </SheetHeader>
        {mode === 'choice' ? (
          <CollectionCreationChoice onChange={setMode} />
        ) : null}
        {mode === 'manual' ? (
          <AddDocumentCollectionForm
            form={controller.form as unknown as AppForm}
            submitError={controller.submitError}
          />
        ) : null}
        {mode === 'template' ? (
          <CollectionTemplateLibrary
            open={controller.open}
            onCreated={() => handleOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function getSheetTitle(mode: 'choice' | 'manual' | 'template', t: Translate) {
  return mode === 'template'
    ? t('documentCollections:templateLibraryTitle')
    : t('documentCollections:addCollectionTitle')
}

function getSheetDescription(
  mode: 'choice' | 'manual' | 'template',
  t: Translate,
) {
  if (mode === 'choice') {
    return t('documentCollections:createChoiceDescription')
  }
  if (mode === 'template') {
    return t('documentCollections:templateLibraryDescription')
  }

  return t('documentCollections:addCollectionDescription')
}
